import React, { useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import { useClient } from '../context/ClientContext';
import apiService from '../services/api.service';

const timeout = (ms) => new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms));

async function timedFetch(checker, ms = 3000) {
  const start = performance.now();
  try {
    const res = await Promise.race([checker(), timeout(ms)]);
    const end = performance.now();
    return { ok: true, ms: Math.round(end - start), data: res };
  } catch (err) {
    const end = performance.now();
    return { ok: false, ms: Math.round(end - start), error: err?.message || 'error' };
  }
}

function collectEnv() {
  const env = {};
  for (const key in process.env) {
    if (Object.prototype.hasOwnProperty.call(process.env, key)) {
      if (key === 'NODE_ENV' || key.startsWith('REACT_APP_')) {
        env[key] = process.env[key];
      }
    }
  }
  return env;
}

function HealthRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid #eee' }}>
      <div style={{ color: '#555' }}>{label}</div>
      <div style={{ fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right' }}>{value}</div>
    </div>
  );
}

export default function HealthCheck() {
  const { tenant } = useTenant();
  const { isAuthenticated, loading: authLoading } = useClient();
  const [apiStatus, setApiStatus] = useState({ ok: null });
  const [storageStatus, setStorageStatus] = useState({ localStorage: null, cookies: null });
  const [now, setNow] = useState(Date.now());

  const timeOrigin = useMemo(() => (window.performance?.timeOrigin ? new Date(window.performance.timeOrigin) : new Date()), []);
  const uptimeMs = now - timeOrigin.getTime();

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Storage diagnostics
    try {
      const key = `hc_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(key, '1');
      const read = localStorage.getItem(key) === '1';
      localStorage.removeItem(key);
      setStorageStatus((s) => ({ ...s, localStorage: read }));
    } catch {
      setStorageStatus((s) => ({ ...s, localStorage: false }));
    }

    try {
      document.cookie = `hc_test=1; path=/; max-age=60`;
      const has = document.cookie.includes('hc_test=1');
      // best-effort cleanup
      document.cookie = 'hc_test=; Max-Age=0; path=/';
      setStorageStatus((s) => ({ ...s, cookies: has }));
    } catch {
      setStorageStatus((s) => ({ ...s, cookies: false }));
    }
  }, []);

  useEffect(() => {
    // API diagnostics (optional)
    const baseUrl = tenant?.config?.apiUrl || process.env.REACT_APP_API_BASE_URL || '';
    if (!baseUrl) {
      setApiStatus({ ok: null, reason: 'No API base URL configured' });
      return;
    }

    const candidates = ['/health', '/healthz', '/status'];

    (async () => {
      for (const path of candidates) {
        // Use apiService to preserve headers/interceptors; ensure relative path
        const res = await timedFetch(() => apiService.get(path).then((d) => d), 4000);
        if (res.ok) {
          setApiStatus({ ok: true, endpoint: path, latencyMs: res.ms, sample: safeSample(res.data) });
          return;
        }
      }
      setApiStatus({ ok: false, reason: 'All probes failed' });
    })();
  }, [tenant?.config?.apiUrl]);

  const env = useMemo(() => collectEnv(), []);

  const data = useMemo(() => ({
    app: {
      nodeEnv: env.NODE_ENV || 'unknown',
      version: env.REACT_APP_VERSION || 'unknown',
      buildTime: env.REACT_APP_BUILD_TIME || 'unknown',
      commit: env.REACT_APP_COMMIT_SHA || 'unknown',
      mode: process.env.NODE_ENV,
    },
    runtime: {
      timeOrigin: timeOrigin.toISOString(),
      now: new Date(now).toISOString(),
      uptimeMs,
      online: navigator.onLine,
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
    },
    tenant: {
      locale: tenant?.locale,
      featureFlags: tenant?.config?.features || {},
      apiBaseUrl: tenant?.config?.apiUrl || env.REACT_APP_API_BASE_URL || null,
      contentKeys: tenant?.content ? Object.keys(tenant.content) : [],
    },
    auth: {
      isAuthenticated,
      loading: authLoading,
    },
    storage: storageStatus,
    api: apiStatus,
    env: Object.fromEntries(Object.entries(env).filter(([k]) => k !== 'NODE_ENV')),
  }), [env, timeOrigin, now, uptimeMs, tenant, isAuthenticated, authLoading, storageStatus, apiStatus]);

  return (
    <div style={{ maxWidth: 900, margin: '24px auto', padding: '16px' }}>
      <h1 style={{ marginBottom: 4 }}>Health Check</h1>
      <div style={{ color: '#666', marginBottom: 16 }}>Detailed diagnostics for troubleshooting and monitoring.</div>

      <section style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <HealthRow label="Status" value={apiStatus.ok === false ? 'DEGRADED' : 'OK'} />
        <HealthRow label="Uptime" value={`${Math.floor(uptimeMs/1000)}s`} />
        <HealthRow label="API" value={apiStatus.ok === null ? 'Not configured' : apiStatus.ok ? `UP (${apiStatus.endpoint}, ${apiStatus.latencyMs}ms)` : 'DOWN'} />
        <HealthRow label="Auth" value={authLoading ? 'Checking…' : (isAuthenticated ? 'Authenticated' : 'Guest')} />
        <HealthRow label="Locale" value={tenant?.locale || 'unknown'} />
        <HealthRow label="Online" value={navigator.onLine ? 'Yes' : 'No'} />
      </section>

      {apiStatus.ok && apiStatus.sample && (
        <section style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>API Sample Response</div>
          <pre style={{ background: '#0b1021', color: '#d6deeb', padding: 12, borderRadius: 8, overflow: 'auto' }}>
            {JSON.stringify(apiStatus.sample, null, 2)}
          </pre>
        </section>
      )}

      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 600 }}>Full Payload</div>
          <button onClick={() => navigator.clipboard.writeText(JSON.stringify(data, null, 2))}>
            Copy JSON
          </button>
        </div>
        <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, overflow: 'auto' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </section>

      <div style={{ color: '#888', marginTop: 12, fontSize: 12 }}>
        Note: This page avoids exposing secrets (tokens, cookies). Intended for diagnostics only.
      </div>
    </div>
  );
}

function safeSample(data) {
  // Remove potentially sensitive fields shallowly
  if (!data || typeof data !== 'object') return data;
  const clone = { ...data };
  delete clone.token;
  delete clone.access_token;
  delete clone.refresh_token;
  delete clone.secret;
  delete clone.password;
  return clone;
}
